/* eslint-disable @typescript-eslint/no-explicit-any */
// store/features/smart-columns.feature.tsx
import { SmartColumnType } from '@/types';
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithAuth } from '../base-query';

export interface SmartColumnTask {
  task_id: string;
  column_type: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  total_leads: number;
  processed_leads: number;
  failed_leads: number;
  progress_percentage: number;
  started_at?: string;
  completed_at?: string;
}

export interface SmartColumnResult {
  id: string;
  lead_id: string;
  lead_list_id: string;
  column_type: string;
  column_name?: string;
  custom_prompt?: string;
  status: string;
  original_value: any;
  processed_value: any;
  column_metadata: Record<string, any>;
  error_message?: string;
  processing_time_ms?: number;
  created_at: string;
}

export interface ProcessSmartColumnsRequest {
  lead_list_id: string;
  column_types: SmartColumnType[];
  custom_columns?: Array<{
    name: string;
    prompt: string;
  }>;
  metadata?: Record<string, unknown>;
  input_mappings_by_column?: Record<string, Record<string, string>>;
  lead_ids?: string[];
  lead_scope?: 'all' | 'subset';
  /** "Only run if" FilterGroup. When set, the backend evaluates each
   *  resolved lead against these conditions before running and skips
   *  (no run, no charge) any lead that doesn't match. Missing/empty =
   *  run all. Sent alongside lead_ids so the condition survives on the
   *  server as the source of truth for which leads the run targets. */
  execution_conditions?: {
    operator: 'AND' | 'OR';
    conditions: Array<{
      field: string;
      operator: string;
      value?: string | number | boolean | string[];
    }>;
  } | null;
}

export interface ColumnConfig {
  column_type: string;
  column_id?: string; // For CSV: original field key (e.g., "company_size")
  column_name?: string;
  display_order: number;
  is_visible?: boolean;
  width?: number;
  source?: 'csv' | 'smart' | 'standard'; // Column source type
  enabled?: boolean;
  execution_conditions?: {
    operator: 'AND' | 'OR';
    conditions: Array<{
      id: string;
      field: string;
      operator: string;
      value?: string | number | boolean | string[];
    }>;
  } | null;
}

export interface SmartColumnConfig {
  lead_list_id: string;
  user_id: string;
  columns: ColumnConfig[];
  created_at?: string;
  modified_at?: string;
}

export interface ProcessSmartColumnsResponse {
  task_ids: Record<string, string>;
}

export interface GetResultsParams {
  leadListId: string;
  columnType?: SmartColumnType;
  columnName?: string;
  leadId?: string;
  /** Only these leads' results (the API caps it at 500). */
  leadIds?: string[];
  skip?: number;
  limit?: number;
}

export interface SmartColumnResultsResponse {
  results: SmartColumnResult[];
  total: number;
  processed: number;
  failed: number;
}

/**
 * Company-scope smart-column result row. Mirrors {@link SmartColumnResult}
 * but is keyed by ``company_id`` instead of ``lead_id`` and always carries
 * ``target_type: "company"``. Produced by company-scope agent runs
 * ("Research" / custom agents) and served from the dedicated
 * ``/ai/smart-columns/company-results/{lead_list_id}`` endpoint.
 *
 * Kept structurally separate from the leads result type so the leads path
 * (keyed by lead_id) stays untouched.
 */
export interface CompanyColumnResult {
  id: string;
  company_id: string;
  lead_list_id: string;
  target_type: 'company';
  column_type: string;
  column_name?: string | null;
  status: string;
  original_value: any;
  processed_value: any;
  column_metadata: Record<string, any>;
  error_message?: string | null;
  processing_time_ms?: number;
  created_at: string;
}

export interface GetCompanyResultsParams {
  leadListId: string;
  columnType?: string;
  columnName?: string;
  skip?: number;
  limit?: number;
}

export interface CompanyColumnResultsResponse {
  results: CompanyColumnResult[];
  total: number;
  processed: number;
  failed: number;
}

export interface DeleteSmartColumnParams {
  columnType: SmartColumnType;
  leadListId: string;
  columnName?: string;
}

export interface DeleteSmartColumnResponse {
  lead_list_id: string;
  deleted_count: number;
}

export interface DeleteCustomColumnParams {
  leadListId: string;
  columnId?: string;
  columnName?: string;
}

export interface DeleteCustomColumnResponse {
  message: string;
  lead_list_id: string;
  column_id?: string;
  column_name?: string;
  deleted_count: number;
}

// Synchronous processing types
export const smartColumnsApi = createApi({
  reducerPath: 'smartColumnsApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['SmartColumnTask', 'SmartColumnResults', 'SmartColumnConfig'],
  endpoints: (builder) => ({
    // Process smart columns (async via worker queue)
    processSmartColumns: builder.mutation<
      ProcessSmartColumnsResponse,
      ProcessSmartColumnsRequest
    >({
      query: (payload) => ({
        url: '/ai/smart-columns/process',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_, __, { lead_list_id }) => [
        { type: 'SmartColumnResults', id: lead_list_id },
      ],
      async onQueryStarted({ lead_list_id }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Invalidate lead list data so new column config appears immediately
          const { leadListsApi } = await import('./lead-lists.feature');
          dispatch(
            leadListsApi.util.invalidateTags([
              { type: 'LeadListLeads', id: lead_list_id },
              { type: 'LeadList', id: lead_list_id },
            ])
          );
        } catch {
          // Mutation failed, no need to invalidate
        }
      },
    }),

    getSmartColumnTask: builder.query<
      SmartColumnTask,
      string | { taskId: string; organizationId: string }
    >({
      query: (arg) =>
        typeof arg === 'string'
          ? `/ai/smart-columns/tasks/${arg}`
          : {
              url: `/ai/smart-columns/tasks/${arg.taskId}`,
              headers: { 'X-Cleanlist-Expected-Org': arg.organizationId },
            },
      providesTags: (_, __, arg) => [
        {
          type: 'SmartColumnTask',
          id: typeof arg === 'string' ? arg : arg.taskId,
        },
      ],
    }),

    getSmartColumnResults: builder.query<
      SmartColumnResultsResponse,
      GetResultsParams
    >({
      query: ({
        leadListId,
        columnType,
        columnName,
        leadId,
        leadIds,
        skip = 0,
        limit = 100,
      }) => {
        const params = new URLSearchParams({
          skip: skip.toString(),
          limit: limit.toString(),
        });

        if (columnType) params.append('column_type', columnType);
        if (columnName) params.append('column_name', columnName);
        if (leadId) params.append('lead_id', leadId);
        if (leadIds?.length) params.append('lead_ids', leadIds.join(','));

        return `/ai/smart-columns/results/${leadListId}?${params}`;
      },
      providesTags: (_, __, { leadListId }) => [
        { type: 'SmartColumnResults', id: leadListId },
      ],
    }),

    // Get company-scope results (mirrors getSmartColumnResults but keyed by
    // company_id and served from a dedicated endpoint). Tagged under a
    // namespaced ``company:<leadListId>`` id so it invalidates independently
    // of the leads results for the same list.
    getCompanyColumnResults: builder.query<
      CompanyColumnResultsResponse,
      GetCompanyResultsParams
    >({
      // Default to a high row cap so company agent cells past the first 100
      // companies aren't left blank on larger lists. True pagination
      // (skip/limit windowing in the table) is a follow-up.
      query: ({
        leadListId,
        columnType,
        columnName,
        skip = 0,
        limit = 1000,
      }) => {
        const params = new URLSearchParams({
          skip: skip.toString(),
          limit: limit.toString(),
        });

        if (columnType) params.append('column_type', columnType);
        if (columnName) params.append('column_name', columnName);

        return `/ai/smart-columns/company-results/${leadListId}?${params}`;
      },
      providesTags: (_, __, { leadListId }) => [
        { type: 'SmartColumnResults', id: `company:${leadListId}` },
      ],
    }),

    deleteSmartColumn: builder.mutation<
      DeleteSmartColumnResponse,
      DeleteSmartColumnParams
    >({
      query: ({ columnType, leadListId, columnName }) => {
        const params = columnName
          ? `?column_name=${encodeURIComponent(columnName)}`
          : '';
        return {
          url: `/ai/smart-columns/${columnType}/lead-list/${leadListId}${params}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: (_, __, { leadListId }) => [
        { type: 'SmartColumnResults', id: leadListId },
      ],
    }),

    updateSmartColumnConfig: builder.mutation<
      { message: string; config: SmartColumnConfig },
      { leadListId: string; columns: ColumnConfig[] }
    >({
      query: ({ leadListId, columns }) => ({
        url: `/ai/smart-columns/config/${leadListId}`,
        method: 'PUT',
        body: { columns },
      }),
      invalidatesTags: (_, __, { leadListId }) => [
        { type: 'SmartColumnConfig', id: leadListId },
      ],
    }),

    // --- Unified Column Config Endpoints ---

    // Get unified column configuration (smart + CSV + standard)
    getColumnConfig: builder.query<SmartColumnConfig, string>({
      query: (leadListId) => `/column-config/${leadListId}`,
      providesTags: (_, __, leadListId) => [
        { type: 'SmartColumnConfig', id: leadListId },
      ],
    }),

    // Update unified column configuration
    updateColumnConfig: builder.mutation<
      { message: string; config: SmartColumnConfig },
      { leadListId: string; columns: ColumnConfig[] }
    >({
      query: ({ leadListId, columns }) => ({
        url: `/column-config/${leadListId}`,
        method: 'PUT',
        body: { columns },
      }),
      invalidatesTags: (_, __, { leadListId }) => [
        { type: 'SmartColumnConfig', id: leadListId },
      ],
    }),

    // Unified rename column (works for both smart and CSV columns)
    renameColumn: builder.mutation<
      {
        message: string;
        lead_list_id: string;
        column_type: string;
        column_id?: string;
        old_name?: string;
        new_name: string;
      },
      {
        leadListId: string;
        columnType: string;
        columnId?: string;
        oldName?: string;
        newName: string;
      }
    >({
      query: ({ leadListId, columnType, columnId, oldName, newName }) => ({
        url: `/column-config/${leadListId}/rename`,
        method: 'PUT',
        body: {
          column_type: columnType,
          column_id: columnId,
          old_name: oldName,
          new_name: newName,
        },
      }),
      invalidatesTags: (_, __, { leadListId }) => [
        { type: 'SmartColumnConfig', id: leadListId },
        { type: 'SmartColumnResults', id: leadListId },
      ],
    }),

    // Update a smart column's processed value (e.g. user edits cold intro email)
    updateSmartColumnValue: builder.mutation<
      { message: string; id: string; lead_id: string; processed_value: any },
      {
        leadListId: string;
        leadId: string;
        columnType: string;
        columnName?: string;
        processedValue: any;
      }
    >({
      query: ({
        leadListId,
        leadId,
        columnType,
        columnName,
        processedValue,
      }) => ({
        url: `/ai/smart-columns/value/${leadListId}`,
        method: 'PATCH',
        body: {
          lead_id: leadId,
          column_type: columnType,
          column_name: columnName,
          processed_value: processedValue,
        },
      }),
      invalidatesTags: (_, __, { leadListId }) => [
        { type: 'SmartColumnResults', id: leadListId },
      ],
    }),

    // Delete custom column (CSV import columns)
    deleteCustomColumn: builder.mutation<
      DeleteCustomColumnResponse,
      DeleteCustomColumnParams
    >({
      query: ({ leadListId, columnId, columnName }) => {
        const params = new URLSearchParams();
        if (columnId) params.append('column_id', columnId);
        if (columnName) params.append('column_name', columnName);
        return {
          url: `/column-config/${leadListId}/custom-column?${params.toString()}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: (_, __, { leadListId }) => [
        { type: 'SmartColumnConfig', id: leadListId },
      ],
      // Cross-invalidate lead list tags to refetch custom_column_config
      async onQueryStarted({ leadListId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Import leadListsApi dynamically to avoid circular dependencies
          const { leadListsApi } = await import('./lead-lists.feature');
          dispatch(
            leadListsApi.util.invalidateTags([
              { type: 'LeadListLeads', id: leadListId },
              { type: 'LeadList', id: leadListId },
            ])
          );
        } catch {
          // Mutation failed, no need to invalidate
        }
      },
    }),
  }),
});

export const {
  useProcessSmartColumnsMutation,
  useGetSmartColumnTaskQuery,
  useLazyGetSmartColumnTaskQuery,
  useGetSmartColumnResultsQuery,
  useLazyGetSmartColumnResultsQuery,
  useGetCompanyColumnResultsQuery,
  useDeleteSmartColumnMutation,
  useUpdateSmartColumnConfigMutation,
  useUpdateSmartColumnValueMutation,
  // Unified column config hooks
  useGetColumnConfigQuery,
  useUpdateColumnConfigMutation,
  useRenameColumnMutation,
  useDeleteCustomColumnMutation,
} = smartColumnsApi;
